import { useTheme } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  Dialog,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { changePasswordSchema } from './validations';
import { Logs, changePassword } from '../../services';
import { ErrorText } from '../../components';
import { Check, ChangePassword } from '../../assets';
import { logoutUser } from '../../store/reducers/auth';
import { notification } from 'antd';

export const ResetPassword = () => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [invalidCredentials, setInvalidCredentials] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const form = useForm({
    resolver: yupResolver(changePasswordSchema),
    mode: 'all',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    control,
  } = form;

  const handleChangePassword = async (payload) => {
    try {
      const res = await changePassword(payload);
      if (res.status === 200) {
        setInvalidCredentials('');
        setShowSuccess(true);
        notification.success({
          message: "Password Updated!",
          description: "Password has been changed Successfully use your new password to login."
        })
        navigate('/login');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error.message;
      setInvalidCredentials(errorMessage);
      if (errorMessage === 'Your new password must be different') {
        setError('passwordConfirmation', {
          type: 'manual',
          message: errorMessage,
        });
      }
      Logs(error);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const newPassword = useWatch({ control, name: 'password' });
  const confirmPassword = useWatch({ control, name: 'passwordConfirmation' });

  useEffect(() => {
    if (newPassword && confirmPassword && newPassword === confirmPassword) {
      clearErrors('passwordConfirmation');
      setInvalidCredentials('');
    }
  }, [newPassword, confirmPassword, clearErrors]);

  return (
    <>
     <Box
  component="form"
  onSubmit={handleSubmit(handleChangePassword)}
  sx={{
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
    p: 4,
    
  }}
>
  {/* Left Side - Form */}
  <Stack spacing={4} sx={{ flex: 1 }}>
    <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
      Change Your Password
    </Typography>

    {/* Current Password */}
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: '1.4rem',}}>
        Current Password
      </Typography>
      <TextField
        fullWidth
        id="currentPassword"
        type={showPassword.current ? 'text' : 'password'}
        {...register('currentPassword')}
        sx={{
          borderRadius: '0.8rem',
          background: theme.palette.background.main,
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.8rem',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => togglePasswordVisibility('current')}
                edge="end"
              >
                {showPassword.current ? (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {!!errors.currentPassword && (
        <ErrorText>{errors.currentPassword.message}</ErrorText>
      )}
    </Box>

    {/* New Password */}
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: '1.4rem', }}>
        New Password
      </Typography>
      <TextField
        fullWidth
        id="password"
        type={showPassword.new ? 'text' : 'password'}
        {...register('password')}
        sx={{
          borderRadius: '0.8rem',
          background: theme.palette.background.main,
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.8rem',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => togglePasswordVisibility('new')}
                edge="end"
              >
                {showPassword.new ? (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {!!errors.password && (
        <ErrorText>{errors.password.message}</ErrorText>
      )}
    </Box>

    {/* Confirm Password */}
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: '1.4rem', }}>
        Confirm Password
      </Typography>
      <TextField
        fullWidth
        id="passwordConfirmation"
        type={showPassword.confirm ? 'text' : 'password'}
        {...register('passwordConfirmation')}
        sx={{
          borderRadius: '0.8rem',
          background: theme.palette.background.main,
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.8rem',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => togglePasswordVisibility('confirm')}
                edge="end"
              >
                {showPassword.confirm ? (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {!!errors.passwordConfirmation && (
        <ErrorText>{errors.passwordConfirmation.message}</ErrorText>
      )}
      {invalidCredentials && !errors.passwordConfirmation && (
        <ErrorText>{invalidCredentials}</ErrorText>
      )}
    </Box>

    {/* Submit Button */}
    <Button
      sx={{ alignSelf: 'flex-start', mt: 2, px: 4, py: 1.5, fontSize: '1.2rem', fontSize: '1.4rem', }}
      variant="contained"
      type="submit"
    >
      Change Password
    </Button>
  </Stack>

  {/* Right Side - Illustration */}
  <Box
    component="img"
    src={ChangePassword}
    sx={{
      flex: 0.9,
      maxWidth: '40%',
      borderRadius: 2,
      boxShadow: 2,
    }}
  />
</Box>

    </>
  );
};
